import React, { useState, useEffect, useRef } from 'react';
import { Table, Input, Select, Option, Tooltip } from '@mui/joy';
import CustomPagination from '../components/CustomPagination';
import { productWeightService } from '../api/productWeight';
import { customerService } from '../api/customer';
import { productService } from '../api/product';
import { Customer } from '../api/customer';
import { Product } from '../api/product';
import { PAGE_TRANSLATIONS } from '../conf';
import { formatThaiDate, formatWeight } from '@/util';

// Helper function for Thai date formatting

const ProductWeightPage: React.FC = () => {
  const [productWeights, setProductWeights] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fetchInitiatedRef = useRef(false);
  const customersProductsInitiatedRef = useRef(false);

  const constructWhereData = (searchText: string, startDate: string, endDate: string, selectedCustomer: number | null, selectedProduct: number | null) => {
    let where = "(_.code ILIKE :searchText OR _.headLicensePlate ILIKE :searchText)"
    if (startDate) where += " AND _.dateTime >= :startDate";
    if (endDate) where += " AND _.dateTime <= :endDate";
    if (selectedCustomer) where += " AND _.customer.id = :customerId";
    if (selectedProduct) where += " AND _.product.id = :productId";
    const params = {
      searchText: `%${searchText}%`,
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(selectedCustomer && { customerId: selectedCustomer }),
      ...(selectedProduct && { productId: selectedProduct })
    };
    return { where, params };
  };

  // Fetch customers and products only once on mount
  useEffect(() => {
    if (customersProductsInitiatedRef.current) {
      return;
    }
    customersProductsInitiatedRef.current = true;

    const fetchCustomersAndProducts = async () => {
      try {
        const [customerResponse, productResponse] = await Promise.all([
          customerService.listAll(),
          productService.listAll()
        ]);
        setCustomers(customerResponse);
        setProducts(productResponse);
      } catch (error) {
        console.error('Failed to fetch customers or products', error);
      }
    };

    fetchCustomersAndProducts();
  }, []);

  // Combined effect for fetching product weights with pagination
  useEffect(() => {
    if (!fetchInitiatedRef.current) {
      fetchInitiatedRef.current = true;
      return;
    }

    const loadProductWeights = async () => {
      try {
        const whereData = constructWhereData(searchText, startDate, endDate, selectedCustomer, selectedProduct);
        const options = {
          max: rowsPerPage,
          offset: (currentPage - 1) * rowsPerPage,
          sort: '_.code',
          order: 'ASC' as 'ASC'
        };

        const response = await productWeightService.where(whereData, options);
        setProductWeights(response.data.data);
        setTotalCount(response.data.totalCount);
      } catch (error) {
        console.error('Failed to load product weights', error);
      }
    };

    loadProductWeights();
  }, [currentPage, rowsPerPage, searchText, startDate, endDate, selectedCustomer, selectedProduct]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
    setCurrentPage(1);
  };

  const handleCustomerChange = (newValue: string | null) => {
    setSelectedCustomer(newValue ? parseInt(newValue, 10) : null);
    setCurrentPage(1);
  };

  const handleProductChange = (newValue: string | null) => {
    setSelectedProduct(newValue ? parseInt(newValue, 10) : null);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  const totalNetWeight = productWeights.reduce((sum, weight) => {
    return sum + (weight.weight - weight.truckWeight);
  }, 0);

  return (
    <div>
      <h2>{PAGE_TRANSLATIONS.PRODUCT_WEIGHT}</h2>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="searchText">{PAGE_TRANSLATIONS.SEARCH}</label>
          <Input
            id="searchText"
            ref={inputRef}
            placeholder={`${PAGE_TRANSLATIONS.CODE}, ${PAGE_TRANSLATIONS.LICENSE_PLATE}`}
            value={searchText}
            onChange={handleFilterChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="startDate">{PAGE_TRANSLATIONS.START_DATE}</label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="endDate">{PAGE_TRANSLATIONS.END_DATE}</label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="customerSelect">{PAGE_TRANSLATIONS.CUSTOMERS}</label>
          <Select
            id="customerSelect"
            value={selectedCustomer !== null ? selectedCustomer.toString() : ''}
            onChange={(_e, newValue) => handleCustomerChange(newValue)}
            sx={{ minWidth: 200 }}
          >
            <Option value="">{PAGE_TRANSLATIONS.ALL_CUSTOMERS}</Option>
            {customers.map(customer => (
              <Option key={customer.id} value={customer.id.toString()}>{customer.name}</Option>
            ))}
          </Select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="productSelect">{PAGE_TRANSLATIONS.PRODUCTS}</label>
          <Select
            id="productSelect"
            value={selectedProduct !== null ? selectedProduct.toString() : ''}
            onChange={(_e, newValue) => handleProductChange(newValue)}
            sx={{ minWidth: 200 }}
          >
            <Option value="">{PAGE_TRANSLATIONS.ALL_PRODUCTS}</Option>
            {products.map(product => (
              <Option key={product.id} value={product.id.toString()}>{product.name}</Option>
            ))}
          </Select>
        </div>
      </div>
      <Table>
        <thead>
          <tr>
            {/* <th>ID</th> */}
            <th>{PAGE_TRANSLATIONS.CODE}</th>
            <th>{PAGE_TRANSLATIONS.PRODUCT_NAME}</th>
            <th>{PAGE_TRANSLATIONS.CUSTOMER_NAME}</th>            
            <th>{PAGE_TRANSLATIONS.DATE}</th>
            <th>{PAGE_TRANSLATIONS.ALL_LICENSE_PLATE}</th>
            <th>{PAGE_TRANSLATIONS.NET_WEIGHT}</th>
            <th>{PAGE_TRANSLATIONS.WEIGHED_BY}</th>
          </tr>
        </thead>
        <tbody>
          {productWeights.length > 0 ? (
            productWeights.map((weight) => (
              <tr key={weight.id}>
                {/* <td>{weight.id}</td> */}
                <td>{weight.code}</td>
                <td>{weight.product.name}</td>
                <td>{weight.customer.name}</td>                
                <td>{formatThaiDate(weight.dateTime)}</td>
                <td>{`${weight.headLicensePlate} / ${weight.tailLicensePlate}`}</td>
                <td>
                  <Tooltip
                    title={formatThaiDate(weight.truckWeightDateTime)}
                    variant="soft"
                  >
                    <span>{formatWeight(weight.weight - weight.truckWeight)}{weight.truckWeightManualFlag && '*'}</span>
                  </Tooltip>
                </td>
                <td>{weight.weightBy}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center' }}>{PAGE_TRANSLATIONS.NO_DATA}</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
            <td colSpan={5} style={{ textAlign: 'right' }}>ผลรวม:</td>
            <td>{formatWeight(totalNetWeight)}</td>
            <td></td>
          </tr>
        </tfoot>
      </Table>
      <CustomPagination
        count={totalCount}
        page={totalCount > 0 ? currentPage : 0}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

export default ProductWeightPage; 